import { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Modal, Tooltip } from 'antd';
import { WrappedComponentProps, injectIntl, FormattedMessage, useIntl } from 'react-intl';
import { RouteComponentProps, useHistory, withRouter } from 'react-router-dom';
import { get, isEmpty } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import { getAccountUsernameFromPath, RootState, UrlUtils, VACATIONS_PAGE } from '@share/utils';
import { DEAL_HOME_LABEL, Routes } from '@share/constants';
import { ILoginState } from '@share/store/slices';

import Image from '@assets/images/availability-status.png';

import './style.scss';
import { BookingDealStatusEnum } from '@share/common-types';
import { WhiteButton } from '../white-button';

export enum Type {
  CONDOS,
  HOTELS,
  VACATION_RENTALS,
  WEEKS,
  GETAWAYS,
  GETAWAYS_CONDOS,
  GETAWAYS_HOTELS,
}

interface IProps {
  hasSoldout: boolean;
  isUnavailable: boolean;
  dealStatus?: string;
  isDetails?: boolean;

  propertyId: string | undefined;
  bookingErrorCode: string;
  type: Type;
}

const modalWidth = 400;

export function ModalAvailabilityStatus(props: IProps) {
  const { hasSoldout, isUnavailable, dealStatus, propertyId, bookingErrorCode, type, isDetails,  } = props;
  
  const loginStore = useSelector((state: RootState) => state.loginStore);

  const { account } = loginStore;


  const intl = useIntl();

  const history = useHistory();

  const accountName = useMemo(() => getAccountUsernameFromPath(history), [history]);

  const [backHomeType, setBackHomeType] = useState<string>('');
  const [backHomeTypeSecondaryType, setBackHomeTypeSecondaryType] = useState<string | undefined>('');
  const [forceCloseModal, setForceCloseModal] = useState<boolean>(false);

  useEffect(() => {
    const values = UrlUtils.getValues();
    if (values[DEAL_HOME_LABEL]) {
      setBackHomeType(values[DEAL_HOME_LABEL] as string);
    }
  }, []);

  useEffect(() => {
    setBackHomeTypeSecondaryType(get(backHomeType?.split('-'), '[1]'));
  }, [backHomeType]);

  const routeDetails = useMemo(() => type === Type.WEEKS ? 
                                        Routes.WeeksDetails :
                                        type === Type.CONDOS ?
                                            Routes.Condo :
                                            Routes.Hotel, 
                              [type]);

  const routeSearch =  useMemo(() => type === Type.GETAWAYS ? 
                                        !isEmpty(backHomeTypeSecondaryType) ? 
                                            'PREMIERESCAPES' === backHomeTypeSecondaryType?.toUpperCase() ? 
                                                `${Routes.GetawayCondos}/premierescapes` : 
                                                `${Routes.GetawayCondos}/valuegetaways` :
                                            Routes.Getaways :
                                        type === Type.GETAWAYS_HOTELS ?
                                            Routes.GetawayStaycation :
                                            type === Type.GETAWAYS_CONDOS ?
                                                !isEmpty(backHomeTypeSecondaryType) ? 
                                                'PREMIERESCAPES' === backHomeTypeSecondaryType?.toUpperCase() ? 
                                                    `${Routes.GetawayCondos}/premierescapes` : 
                                                    `${Routes.GetawayCondos}/valuegetaways` :
                                                Routes.Getaways :
                                                type === Type.WEEKS ? 
                                                        Routes.WeeksSearch :
                                                        type === Type.CONDOS ?
                                                            Routes.CondoSearch :
                                                            Routes.Search, 
                              [type, backHomeTypeSecondaryType]);
    

  const vacationRentalsRoute = useMemo(() => type === Type.VACATION_RENTALS ? `/${VACATIONS_PAGE}` : '',[type]);

  const handleGoToDetails = () => {
    history.push(`/${accountName}${routeDetails}/${propertyId}${vacationRentalsRoute}${location.search}`);
  }
  const handleGoToSearch = () => {
    history.push(`/${accountName}${routeSearch}${vacationRentalsRoute}${location.search}`);
  }

  const background = account?.buttonColor;
  const border = account?.buttonBorder;
  const color = account?.buttonTextColor;
  const borderRadius = account?.buttonBorderRadius;
  const buttonTextTransform = account?.buttonTextTransform;
    
  const styleButtonBorderRadius = !isEmpty(borderRadius) ? { borderRadius: `${borderRadius}px` } : {};  
  const styleButtonnTextTransform = (buttonTextTransform && !isEmpty(buttonTextTransform)) ? { ...styleButtonBorderRadius, textTransform: buttonTextTransform as any } : { ...styleButtonBorderRadius };  
  const styleButtonBorder = !isEmpty(border)? { ...styleButtonnTextTransform, border } : { ...styleButtonnTextTransform };  
  const styleButtonColor = !isEmpty(color)? { ...styleButtonBorder, color } : { ...styleButtonBorder };  
  const styleButton = !isEmpty(background)? { ...styleButtonColor, background } : { ...styleButtonColor };  

  const isCondos = [Type.CONDOS, Type.GETAWAYS, Type.GETAWAYS_CONDOS].includes(type);
  const isHotels = [Type.HOTELS, Type.VACATION_RENTALS, Type.GETAWAYS_HOTELS].includes(type);
  const isGetaways = [Type.GETAWAYS, Type.GETAWAYS_HOTELS, Type.GETAWAYS_CONDOS].includes(type);

  const messageId = isCondos ? 
                          !isEmpty(dealStatus) ? 
                                dealStatus === BookingDealStatusEnum.PriceChanged ? 
                                    'condo.availability.status.modal.message.price_changed' : 
                                    'condo.availability.status.modal.message.sold_out' : 
                                'condo.availability.status.modal.message' : 
                          isHotels ? 
                                'unit.selected.no.longer.available' :
                                'availability.status.modal.message';

  const messageTooltipId = isCondos && isEmpty(dealStatus) ? 
                                'condo.availability.status.modal.tooltip' : 
                                null; 

  const titleId = isCondos && !isEmpty(dealStatus) ? 
                                dealStatus === BookingDealStatusEnum.PriceChanged ? 
                                    'unit.is.no.available.price_changed' :
                                    'unit.is.no.available.sold_out' :
                                'unit.is.no.available';
  return (
    <div className="price-change-modal">
      <Modal
        className="price-change-modal-wrapper"
        wrapClassName="price-change-modal-wrapper__wrap"
        open={(hasSoldout || isUnavailable || !!dealStatus) && !forceCloseModal}
        footer={null}
        title={null}
        closable={false}
        keyboard={false}
        destroyOnClose={true}
        width={modalWidth}
      >
        <div className="price-change-modal-wrapper__wrapper">
          <img src={Image} alt="" />
          <span className="price-change-modal-wrapper__title"><FormattedMessage id={titleId} /></span>
          <span className="price-change-modal-wrapper__message">
            <FormattedMessage id={messageId} />
            {messageTooltipId && (
              <Tooltip placement="rightBottom" title={intl.formatMessage({ id: messageTooltipId })}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>)}
          </span>
          <div className={`price-change-modal-wrapper__buttons`} style={{ justifyContent: (bookingErrorCode && !isDetails ? 'space-between' : 'center') }}>
            <div className="accept-button" style={styleButton} onClick={handleGoToSearch}><FormattedMessage id={isGetaways ? 'back.to.getaways' : 'availability.status.modal.search_again'} /></div>
            {bookingErrorCode && !isDetails && <div style={styleButton} className="go-to-search-button" onClick={handleGoToDetails}><FormattedMessage id={'availability.status.modal.back_detail'} /></div>}
          </div>

          {dealStatus === BookingDealStatusEnum.PriceChanged && (
            <div className="price-change-modal-wrapper__explanation">
              <div className="go-to-search-button" onClick={() => setForceCloseModal(true)}>
                <FormattedMessage id="accept" />
              </div>
              <FormattedMessage id="new.price.message.by_clicking" />
            </div>)}

          {dealStatus === BookingDealStatusEnum.SoldOut && (
            <div className="price-change-modal-wrapper__explanation">
              <div className="go-to-search-button" onClick={() => setForceCloseModal(true)}>
                <FormattedMessage id="view.other.dates" />
              </div>
            </div>)}
        </div>
      </Modal>
    </div>
  );
}
